import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { getMyPermissions } from "@/services/teamService";

const PermissionsContext = createContext(null);

export function PermissionsProvider({ projectId, children }) {
  const [permissions, setPermissions] = useState(null);
  const [role, setRole] = useState(null);
  const [isOwner, setIsOwner] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchPermissions = useCallback(async () => {
    if (!projectId) {
      setLoading(false);
      return;
    }
    try {
      const data = await getMyPermissions(projectId);
      setPermissions(data.permissions || {});
      setRole(data.role);
      setIsOwner(data.is_owner || false);
    } catch {
      // If no access or error, grant full permissions (owner fallback)
      setPermissions({
        view_all: true, edit_script: true, generate_shots: true,
        manage_characters: true, manage_costumes: true, manage_locations: true,
        shot_blocking: true, budget_scheduling: true, invite_team: true,
      });
      setIsOwner(true);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  const can = useCallback((permission) => {
    if (isOwner) return true;
    return permissions?.[permission] ?? false;
  }, [permissions, isOwner]);

  return (
    <PermissionsContext.Provider value={{ permissions, role, isOwner, can, loading, refresh: fetchPermissions }}>
      {children}
    </PermissionsContext.Provider>
  );
}

export function usePermissions() {
  const ctx = useContext(PermissionsContext);
  if (!ctx) {
    // Return permissive defaults when used outside provider
    return {
      permissions: null, role: null, isOwner: true,
      can: () => true, loading: false, refresh: () => {},
    };
  }
  return ctx;
}


/**
 * Gate component — renders children only if user has the permission.
 * Shows a disabled/locked placeholder if fallback is provided.
 */
export function PermissionGate({ permission, children, fallback = null }) {
  const { can } = usePermissions();
  if (can(permission)) return children;
  return fallback;
}
